import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Scenes() {
  const { projectId } = useParams();
  const [scenes, setScenes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/scenes`);
        setScenes(data);
      } catch (e) { toast.error("Could not load scenes"); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Scene Intelligence</div>
        <h1 className="font-editorial text-4xl mb-8">Scenes</h1>

        {loading ? (
          <div className="text-[#A1A1AA]">Loading…</div>
        ) : scenes.length === 0 ? (
          <EmptyScenes projectId={projectId} />
        ) : (
          <div className="border border-[#1E1E1E]">
            <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-widest text-[#71717A] border-b border-[#1E1E1E] bg-[#0F0F10]">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Heading</div>
              <div className="col-span-2">Location</div>
              <div className="col-span-1">INT/EXT</div>
              <div className="col-span-1">Time</div>
              <div className="col-span-1">Complex.</div>
              <div className="col-span-1"></div>
            </div>
            {scenes.map((s) => (
              <div key={s.id} className="grid grid-cols-12 px-4 py-3 text-sm border-b border-[#1E1E1E] hover:bg-[#141414] group items-center" data-testid={`scene-row-${s.number}`}>
                <div className="col-span-1 font-mono-editor text-[#71717A]">{String(s.number).padStart(2, "0")}</div>
                <div className="col-span-5 font-mono-editor text-[13px] truncate">{s.heading}</div>
                <div className="col-span-2 text-[#A1A1AA] text-xs truncate">{s.location_name}</div>
                <div className="col-span-1 text-xs">
                  <span className={`px-1.5 py-0.5 text-[10px] ${s.int_ext === "INT" ? "bg-[#1E1E1E]" : "bg-[#311847]"}`}>{s.int_ext}</span>
                </div>
                <div className="col-span-1 text-xs text-[#A1A1AA]">{s.time_of_day}</div>
                <div className="col-span-1 text-xs">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map((n) => (
                      <div key={n} className={`w-1.5 h-4 ${n <= s.complexity ? "bg-[#D4AF37]" : "bg-[#27272A]"}`}></div>
                    ))}
                  </div>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Link to={`/app/projects/${projectId}/storyboards?scene=${s.id}`}
                    className="text-[#71717A] group-hover:text-[#D4AF37] transition-colors">
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function EmptyScenes({ projectId }) {
  return (
    <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
      <div className="font-editorial text-2xl">No scenes yet.</div>
      <p className="text-sm text-[#A1A1AA] mt-2">
        Write scene headings in the screenplay — they will appear here as structured scenes.
      </p>
      <Link to={`/app/projects/${projectId}/script`} className="mt-6 inline-block bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium">
        Open the screenplay
      </Link>
    </div>
  );
}
